import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/stores/auth'
import { parishesApi } from '@/api/endpoints/parishes'
import { useUI } from '@/stores/ui'
import { motion, AnimatePresence } from 'framer-motion'

export default function Notes() {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const savedNotesRef = useRef<string>('')
  const [showSnackCustomize, setShowSnackCustomize] = useState(false)
  const [snackMessage, setSnackMessage] = useState('')
  const [savingSnack, setSavingSnack] = useState(false)
  const [displayToTeachers, setDisplayToTeachers] = useState(true)
  const [snackWarningExpanded, setSnackWarningExpanded] = useState(false)
  
  const DEFAULT_SNACK_MESSAGE = "In addition to being watchful for allergens, Teachers should avoid snacks with petrochemical dyes or additives - e.g. Red #40, Blue 5, etc., and TBHQ, BHA, or BHT - as research has shown that these ingredients have a strong tendency to make children hyperactive and lose focus."

  // Load notes on mount
  useEffect(() => {
    if (!user?.parish_id) return

    let mounted = true
    const loadNotes = async () => {
      setLoading(true)
      try {
        const parish = await parishesApi.get(user.parish_id!)
        if (mounted) {
          setNotes(parish.notes || '')
          savedNotesRef.current = parish.notes || ''
          setSnackMessage(parish.snack_warning_message || '')
          setDisplayToTeachers(parish.show_snack_warning_to_teachers ?? true)
        }
      } catch (error) {
        console.error('Failed to load notes:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadNotes()
    return () => { mounted = false }
  }, [user?.parish_id])

  // Auto-save with debounce
  useEffect(() => {
    if (!user?.parish_id || loading) return
    if (notes === savedNotesRef.current) return  // nothing typed yet (or already saved): don't write on load

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout for auto-save (2 seconds after user stops typing)
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true)
      try {
        await parishesApi.update(user.parish_id!, { notes })
        savedNotesRef.current = notes
        // Don't show toast for auto-save to avoid spam
      } catch (error: any) {
        console.error('Failed to save notes:', error)
        addToast({
          message: error.response?.data?.detail || 'Failed to save notes',
          type: 'error',
        })
      } finally {
        setSaving(false)
      }
    }, 2000)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [notes, user?.parish_id, loading, addToast])

  const handleSaveSnackMessage = async () => {
    if (!user?.parish_id) return
    setSavingSnack(true)
    try {
      await parishesApi.update(user.parish_id, {
        snack_warning_message: snackMessage.trim() || undefined, // Empty string becomes undefined to use default
      })
      addToast({ message: 'Snack warning message updated', type: 'success' })
      setShowSnackCustomize(false)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update snack message',
        type: 'error',
      })
    } finally {
      setSavingSnack(false)
    }
  }

  const handleToggleDisplayToTeachers = async (enabled: boolean) => {
    if (!user?.parish_id) return
    setSavingSnack(true)
    try {
      await parishesApi.update(user.parish_id, {
        show_snack_warning_to_teachers: enabled,
      })
      setDisplayToTeachers(enabled)
      addToast({
        message: enabled 
          ? 'Snack warning will now be displayed to Teachers'
          : 'Snack warning will be hidden from Teachers',
        type: 'success',
      })
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update snack warning preference',
        type: 'error',
      })
    } finally {
      setSavingSnack(false)
    }
  }

  if (!user?.parish_id) return null

  return (
    <>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Notes</h4>
        </div>
        <textarea
          className="input-field w-full min-h-[200px] focus-ring resize-y"
          placeholder="Add your notes, inspiration, and miscellaneous items here..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
        />
        {saving && (
          <div className="mt-2 text-xs text-gray-500">Saving...</div>
        )}
        {/* Snack message customization area - minimizable */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setSnackWarningExpanded(!snackWarningExpanded)}
            className="w-full flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <span className="text-yellow-700 font-semibold text-sm">⚠️ Snack Warning</span>
            <span className="text-yellow-700 text-sm">{snackWarningExpanded ? '▼' : '▶'}</span>
          </button>
          {snackWarningExpanded && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-yellow-700 font-semibold text-sm">⚠️</span>
                <p className="text-xs text-yellow-800 flex-1">
                  {snackMessage || DEFAULT_SNACK_MESSAGE}
                </p>
              </div>
              <div className="flex items-center justify-between mt-3">
                {user.role === 'PRIMARY_ADMIN' && (
                  <button
                    type="button"
                    className="text-xs text-yellow-700 hover:text-yellow-800 underline"
                    onClick={() => setShowSnackCustomize(true)}
                  >
                    Customize snack message
                  </button>
                )}
                {(user.role === 'PRIMARY_ADMIN' || user.role === 'DESIGNATED_ADMIN') && (
                  <label className="flex items-center gap-2 cursor-pointer ml-auto">
                    <input
                      type="checkbox"
                      checked={displayToTeachers}
                      onChange={(e) => handleToggleDisplayToTeachers(e.target.checked)}
                      disabled={savingSnack}
                      className="w-4 h-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400 focus:ring-offset-0 cursor-pointer disabled:opacity-50"
                    />
                    <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                      Display to Teachers
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Snack Message Customization Modal */}
      <AnimatePresence>
        {showSnackCustomize && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold mb-4">Customize Snack Warning Message</h3>
              <p className="text-sm text-gray-600 mb-4">
                Customize the message that appears to teachers about snacks. Leave empty to use the default message.
              </p>
              <textarea
                className="input-field w-full min-h-[150px] focus-ring resize-y"
                placeholder={DEFAULT_SNACK_MESSAGE}
                value={snackMessage}
                onChange={(e) => setSnackMessage(e.target.value)}
                disabled={savingSnack}
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowSnackCustomize(false)
                    // Reload original message
                    if (user?.parish_id) {
                      parishesApi.get(user.parish_id).then(parish => {
                        setSnackMessage(parish.snack_warning_message || '')
                      })
                    }
                  }}
                  disabled={savingSnack}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveSnackMessage}
                  disabled={savingSnack}
                >
                  {savingSnack ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

